import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSonaxWidget } from '@/hooks/integrations/useSonaxWidget';
import { useCurrentOrgId } from '@/hooks/auth';
import { buildSipUri } from '@/lib/calls/sipUri';
import { openExternalUri } from '@/lib/calls/openExternalUri';
import { useCallPrefs, type CallProviderId } from './useCallPrefs';

export type { CallProviderId };

export interface CallContext {
  activityId?: string;
  dealId?: string;
  leadId?: string;
}

export type PlaceCallReason =
  | 'no-phone'
  | 'invalid-phone'
  | 'not-configured'
  | 'invalid-template'
  | 'error';

export interface PlaceCallResult {
  ok: boolean;
  provider: CallProviderId;
  reason?: PlaceCallReason;
  /** softphone: número normalizado (para o toast com "copiar"). */
  numero?: string;
}

/**
 * Fachada de discagem: um `placeCall` para os dois providers.
 *
 * webphone  → delega para useSonaxWidget.openWidgetAndCall, sem mudar nada do que existe
 *             hoje. `useSonaxWidget.ts` NÃO é editado; é isso que blinda contra regressão.
 * softphone → monta a URI sip: e entrega ao MicroSIP.
 *
 * O hook do Sonax é montado SEMPRE (regras de hooks). Isso não carrega o script: o widget
 * só é injetado quando alguém chama armSonax(), e o caminho softphone nunca chama.
 *
 * A maioria dos call sites deve usar useClickToCall (que embrulha isto com os toasts).
 */
export function useCallProvider() {
  const { provider, sipTemplate } = useCallPrefs();
  const sonax = useSonaxWidget();
  const { orgId } = useCurrentOrgId();

  const isSoftphone = provider === 'softphone';

  /**
   * Grava a intenção de discagem: é o que permite ao sonax-webhook saber a ORG CORRENTE
   * (o ramal sozinho não desambigua quando o usuário é membro de várias) e amarrar a
   * ligação à atividade certa. Fire-and-forget de propósito — falhar aqui degrada a
   * atribuição, nunca impede a ligação.
   *
   * Enfileira nos DOIS providers: no webphone a intenção assume o papel que
   * sonax_call_queue tinha de carregar o activity_id até o registrador.
   */
  const enqueueIntent = useCallback(
    (phone: string, ctx: CallContext | undefined, forProvider: CallProviderId) => {
      if (!orgId) return;
      void (supabase.rpc as any)('call_dial_intent_enqueue', {
        p_org_id: orgId,
        p_phone: phone,
        p_lead_id: ctx?.leadId ?? null,
        p_deal_id: ctx?.dealId ?? null,
        p_activity_id: ctx?.activityId ?? null,
        p_provider: forProvider,
      }).then(({ error }: { error: unknown }) => {
        if (error) console.warn('[calls] falha ao registrar intenção de discagem:', error);
      });
    },
    [orgId],
  );

  const placeCall = useCallback(
    async (
      phone: string | null | undefined,
      ctx?: CallContext,
    ): Promise<PlaceCallResult> => {
      if (!phone || !phone.trim()) {
        return { ok: false, provider, reason: 'no-phone' };
      }

      if (isSoftphone) {
        const built = buildSipUri(phone, sipTemplate);
        if (!built.ok) {
          return {
            ok: false,
            provider,
            reason: built.reason === 'empty' ? 'no-phone' : built.reason,
          };
        }

        // SÍNCRONO, antes de qualquer await: o Chrome só entrega a URI ao SO enquanto a
        // ativação do gesto do usuário está viva.
        openExternalUri(built.uri);
        enqueueIntent(phone, ctx, 'softphone');

        return { ok: true, provider, numero: built.numero };
      }

      // ----- webphone: comportamento atual, intocado
      if (!sonax.hasScript) {
        return { ok: false, provider, reason: 'not-configured' };
      }

      enqueueIntent(phone, ctx, 'webphone');

      try {
        await sonax.openWidgetAndCall(phone, {
          activityId: ctx?.activityId,
          dealId: ctx?.dealId,
        });
        return { ok: true, provider };
      } catch (error) {
        console.error('[calls] erro ao discar pelo webphone:', error);
        return { ok: false, provider, reason: 'error' };
      }
    },
    [provider, isSoftphone, sipTemplate, sonax, enqueueIntent],
  );

  return {
    provider,
    /** Substitui o `hasScript` dos call sites: em softphone não depende do script Sonax. */
    canCall: isSoftphone ? true : sonax.hasScript,
    isProcessing: isSoftphone ? false : sonax.isProcessing,
    placeCall,
    /** Acesso cru ao webphone — só para o Modo Foco e usos avançados. */
    sonax,
  };
}
